import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { Controller } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup } from '@gem/popups';
import {
  Button,
  ButtonTypes,
  PopupFooter,
  PopupHeader,
  SwitchMenuItem,
  TextField,
} from '@gem/ui-kit';

import { FORM_ID, NAME_MAX_LENGTH } from './NewMeetingPopup.constants';
import { useNewMeetingPopupPresenter } from './NewMeetingPopup.presenter';
import styles from './NewMeetingPopup.styles.m.css';
import { INewMeetingFormData } from './NewMeetingPopup.types';
import { roomNameValidation } from './validation/roomNameValidation';

export const NewMeetingPopup: FC<ICommonPopupsProps> = observer((props) => {
  const { t } = useTranslation();
  const { presenter, form } = useNewMeetingPopupPresenter(props);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, isValidating },
  } = form;

  return (
    <Popup
      isOpened
      onClose={presenter.onClose}
    >
      <PopupHeader
        title={t('new_meeting_modal_header')}
        onClose={presenter.onClose}
      />
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(presenter.onSubmit)}
      >
        <Controller
          control={control}
          name="roomName"
          rules={{
            ...roomNameValidation<INewMeetingFormData>(t('new_meeting_name_min_length_error')),
          }}
          render={({ field, fieldState }): ReactElement => (
            <TextField
              {...field}
              maxLength={NAME_MAX_LENGTH}
              label={t('new_meeting_modal_name_field')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
              className={styles.textField}
            />
          )}
        />

        <Controller
          control={control}
          name="allowGuests"
          render={({ field }): ReactElement => (
            <SwitchMenuItem
              title={t('new_meeting_modal_allow_guests_toogle')}
              subTitle={t('new_meeting_modal_allow_guests_tips')}
              isChecked={field.value}
              onChange={field.onChange}
              className={classNames(styles.menuItem, styles.switch)}
            />
          )}
        />
        <Controller
          control={control}
          name="waitingRoomEnabled"
          render={({ field }): ReactElement => (
            <SwitchMenuItem
              title={t('new_meeting_modal_waiting_room_toogle')}
              subTitle={t('new_meeting_modal_waiting_room_tips')}
              isChecked={field.value}
              onChange={field.onChange}
              className={classNames(styles.menuItem, styles.switch)}
            />
          )}
        />
        <Controller
          control={control}
          name="webinar"
          render={({ field }): ReactElement => (
            <SwitchMenuItem
              title={t('webinar_toggle_title')}
              subTitle={t('webinar_toggle_description')}
              isChecked={field.value}
              onChange={field.onChange}
              className={classNames(styles.menuItem, styles.switch)}
            />
          )}
        />
      </form>
      <PopupFooter>
        <Button
          type={ButtonTypes.Submit}
          form={FORM_ID}
          isLoading={isSubmitting}
          isDisabled={!isValid || isValidating}
        >
          {t('common_create')}
        </Button>
      </PopupFooter>
    </Popup>
  );
});
